if (!customElements.get('craftcard-kit-configurator')) {
  customElements.define(
    'craftcard-kit-configurator',
    class CraftcardKitConfigurator extends HTMLElement {
      constructor() {
        super();
        this.steps = ['base', 'inspection', 'precision', 'liquids', 'advanced', 'frequent', 'summary'];
        this.currentStep = 'base';
        this.hasInitialized = false;
      }

      connectedCallback() {
        this.stepButtons = Array.from(this.querySelectorAll('[data-step-target]'));
        this.panels = Array.from(this.querySelectorAll('[data-kit-panel]'));
        this.cards = Array.from(this.querySelectorAll('[data-kit-card]'));
        this.baseCards = Array.from(this.querySelectorAll('[data-kit-base]'));
        this.accessoryCards = Array.from(this.querySelectorAll('[data-kit-accessory]'));
        this.premiumSwapCards = Array.from(this.querySelectorAll('[data-kit-premium-swap]'));
        this.summaryList = this.querySelector('[data-kit-summary-list]');
        this.miniList = this.querySelector('[data-kit-mini-list]');
        this.mobileList = this.querySelector('[data-kit-mobile-list]');
        this.totalElements = Array.from(
          this.querySelectorAll('[data-kit-total], [data-kit-total-mini], [data-kit-total-mobile], [data-kit-total-mobile-drawer]')
        );
        this.emptySummary = this.querySelector('[data-kit-empty-summary]');
        this.message = this.querySelector('[data-kit-message]');
        this.addButton = this.querySelector('[data-kit-add]');
        this.nextButton = this.querySelector('[data-kit-next]');
        this.prevButton = this.querySelector('[data-kit-prev]');
        this.mobileDrawer = this.querySelector('[data-kit-mobile-drawer]');
        this.mobileSummaryToggle = this.querySelector('[data-kit-mobile-summary-toggle]');
        this.mobileSummaryClose = this.querySelector('[data-kit-mobile-summary-close]');
        this.progressText = this.querySelector('[data-kit-progress-text]');
        this.progressFill = this.querySelector('[data-kit-progress-fill]');
        this.progressBar = this.querySelector('[data-kit-progress-bar]');

        this.stepButtons.forEach((button) => {
          button.addEventListener('click', () => this.showStep(button.dataset.stepTarget, true));
        });

        this.querySelectorAll('[data-variant-select]').forEach((select) => {
          select.addEventListener('change', (event) => this.onVariantChange(event));
          this.updateCardVariant(select.closest('[data-kit-card]'), select.selectedOptions[0]);
        });

        this.cards.forEach((card) => {
          const button = card.querySelector('[data-select-card]');
          let pointerStartX = 0;
          let pointerStartY = 0;
          let pointerMoved = false;

          button?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.toggleCard(card);
          });

          card.addEventListener('pointerdown', (event) => {
            pointerStartX = event.clientX;
            pointerStartY = event.clientY;
            pointerMoved = false;
          });

          card.addEventListener('pointermove', (event) => {
            if (Math.abs(event.clientX - pointerStartX) > 12 || Math.abs(event.clientY - pointerStartY) > 12) {
              pointerMoved = true;
            }
          });

          card.addEventListener('click', (event) => {
            if (pointerMoved) return;
            if (this.shouldIgnoreCardClick(event)) return;
            this.toggleCard(card);
          });

          card.setAttribute('role', 'button');
          card.setAttribute('tabindex', '0');
          card.setAttribute('aria-pressed', card.classList.contains('is-selected') ? 'true' : 'false');

          card.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            if (this.shouldIgnoreCardClick(event)) return;

            event.preventDefault();
            this.toggleCard(card);
          });
        });

        this.querySelectorAll('[data-kit-quantity]').forEach((input) => {
          input.addEventListener('change', () => {
            input.value = Math.max(1, parseInt(input.value || '1', 10));
            this.updateSummary();
          });
        });

        this.nextButton?.addEventListener('click', () => this.goToAdjacentStep(1));
        this.prevButton?.addEventListener('click', () => this.goToAdjacentStep(-1));
        this.addButton?.addEventListener('click', () => this.addToCart());
        this.mobileSummaryToggle?.addEventListener('click', () => this.toggleMobileDrawer(true));
        this.mobileSummaryClose?.addEventListener('click', () => this.toggleMobileDrawer(false));

        this.updateCompatibility();
        this.updateVisibleSteps();
        this.updateSummary();
        this.updateNavigation();
        this.updateProgress();

        this.hasInitialized = true;
      }

      shouldIgnoreCardClick(event) {
        const target = event.target;

        return Boolean(
          target.closest('button') ||
          target.closest('a') ||
          target.closest('select') ||
          target.closest('input') ||
          target.closest('textarea') ||
          target.closest('label')
        );
      }

      onVariantChange(event) {
        const select = event.currentTarget;
        this.updateCardVariant(select.closest('[data-kit-card]'), select.selectedOptions[0]);
        this.updateCompatibility();
        this.updateVisibleSteps();
        this.updateSummary();
      }

      updateCardVariant(card, option) {
        if (!card || !option) return;

        const available = option.dataset.available === 'true';
        card.dataset.variantId = option.value;
        card.dataset.price = option.dataset.price || '0';
        card.dataset.available = String(available);

        const price = card.querySelector('[data-kit-price]');
        if (price) price.textContent = this.formatMoney(parseInt(card.dataset.price || '0', 10));

        const availability = card.querySelector('[data-kit-availability]');
        if (availability) {
          availability.textContent = available ? 'Disponibile' : 'Esaurito';
          availability.classList.toggle('is-unavailable', !available);
        }

        const image = card.querySelector('[data-kit-image]');
        if (image && option.dataset.image) {
          image.src = option.dataset.image;
          image.removeAttribute('srcset');
        }

        const button = card.querySelector('[data-select-card]');

        if (!available) {
          this.setCardSelected(card, false);
          if (button) button.disabled = true;
        } else if (button) {
          button.disabled = false;
        }
      }

      toggleCard(card) {
        const isSelected = card.classList.contains('is-selected');

        if (card.dataset.available !== 'true' && !isSelected) return;

        if (card.matches('[data-kit-base]')) {
          this.baseCards.forEach((baseCard) => this.setCardSelected(baseCard, baseCard === card));
          this.premiumSwapCards.forEach((swapCard) => this.setCardSelected(swapCard, false));
          this.applyPremiumVariantToSelectedBase();
          this.updateCompatibility();
          this.updateVisibleSteps();
          this.showStep(this.nextAvailableStep('base', 1), true);
        } else if (card.matches('[data-kit-premium-swap]')) {
          this.setCardSelected(card, !isSelected);
          this.applyPremiumVariantToSelectedBase();
        } else {
          this.setCardSelected(card, !isSelected);
        }

        this.updateSummary();
        this.updateNavigation();
        this.updateProgress();
      }

      setCardSelected(card, selected) {
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-pressed', selected ? 'true' : 'false');

        const input = card.querySelector('[data-kit-base-input], [data-kit-accessory-input], [data-kit-premium-swap-input]');
        if (input) input.checked = selected;

        const button = card.querySelector('[data-select-card]');

        if (button) {
          if (card.dataset.available === 'true') {
            button.disabled = false;
            button.removeAttribute('disabled');
          }

          if (button.dataset.selectedLabel && button.dataset.unselectedLabel) {
            button.textContent = selected ? button.dataset.selectedLabel : button.dataset.unselectedLabel;
          } else if (card.matches('[data-kit-base]')) {
            button.textContent = selected ? 'Base selezionata' : 'Scegli base';
          } else {
            button.textContent = selected ? 'Rimuovi' : 'Aggiungi';
          }
        }
      }

      selectedBasePath() {
        return this.baseCards.find((card) => card.classList.contains('is-selected'))?.dataset.path || '';
      }

      applyPremiumVariantToSelectedBase() {
        const premiumBase = this.baseCards.find((card) => card.dataset.path === 'premium');
        if (!premiumBase || !premiumBase.classList.contains('is-selected')) return;

        const swapSelected = this.premiumSwapCards.some((card) => !card.hidden && card.classList.contains('is-selected'));

        const variantId = swapSelected
          ? premiumBase.dataset.premiumHydropressVariantId
          : premiumBase.dataset.premiumStandardVariantId;

        const variantTitle = swapSelected
          ? premiumBase.dataset.premiumHydropressVariantTitle
          : premiumBase.dataset.premiumStandardVariantTitle;

        const variantPrice = swapSelected
          ? premiumBase.dataset.premiumHydropressPrice
          : premiumBase.dataset.premiumStandardPrice;

        const variantAvailable = swapSelected
          ? premiumBase.dataset.premiumHydropressAvailable
          : premiumBase.dataset.premiumStandardAvailable;

        if (!variantId) return;

        premiumBase.dataset.variantId = variantId;
        premiumBase.dataset.variantTitle = variantTitle || '';
        premiumBase.dataset.summaryVariantTitle = swapSelected ? 'HydroPress' : variantTitle || '';
        premiumBase.dataset.price = variantPrice || premiumBase.dataset.price || '0';
        premiumBase.dataset.available = String(variantAvailable === 'true');

        const price = premiumBase.querySelector('[data-kit-price]');
        if (price) price.textContent = this.formatMoney(parseInt(premiumBase.dataset.price || '0', 10));

        const availability = premiumBase.querySelector('[data-kit-availability]');
        if (availability) {
          const available = premiumBase.dataset.available === 'true';
          availability.textContent = available ? 'Disponibile' : 'Esaurito';
          availability.classList.toggle('is-unavailable', !available);
        }
      }

      updateCompatibility() {
        const selectedPath = this.selectedBasePath();

        this.accessoryCards.forEach((card) => {
          let compatible = Boolean(selectedPath) && card.dataset[`compatible${this.capitalize(selectedPath)}`] === 'true';

          if (selectedPath === 'premium' && card.matches('[data-kit-hydropress-accessory]')) {
            compatible = false;
          }

          card.hidden = !compatible;

          if (!compatible) {
            this.setCardSelected(card, false);
            return;
          }

          if (card.dataset.defaultSelected === 'true' && card.dataset.available === 'true') {
            this.setCardSelected(card, true);
          }
        });

        this.premiumSwapCards.forEach((card) => {
          const compatible = selectedPath === 'premium' && card.dataset.premiumVariantId;
          card.hidden = !compatible;

          if (!compatible) {
            this.setCardSelected(card, false);
          }
        });

        this.querySelectorAll('[data-kit-panel]').forEach((panel) => {
          const message = panel.querySelector('[data-base-required-message]');
          if (message) message.hidden = Boolean(selectedPath);
        });
      }

      updateVisibleSteps() {
        const selectedPath = this.selectedBasePath();

        this.steps.forEach((step) => {
          const visible = this.isStepVisible(step, selectedPath);
          const button = this.stepButtons.find((stepButton) => stepButton.dataset.stepTarget === step);

          if (button) {
            button.hidden = !visible;
            button.closest('li')?.toggleAttribute('hidden', !visible);
          }

          const panel = this.panels.find((kitPanel) => kitPanel.dataset.kitPanel === step);
          const emptyMessage = panel?.querySelector('[data-kit-step-empty]');
          if (emptyMessage) {
            emptyMessage.hidden = !selectedPath || step === 'base' || step === 'summary' || visible;
          }
        });

        this.renumberVisibleSteps();
        this.updateProgress();

        if (!this.visibleSteps().includes(this.currentStep)) {
          this.showStep(this.nextAvailableStep(this.currentStep, 1) || 'summary', false);
        }
      }

      isStepVisible(step, selectedPath = this.selectedBasePath()) {
        if (step === 'base' || step === 'summary') return true;
        if (!selectedPath) return true;

        const panel = this.panels.find((kitPanel) => kitPanel.dataset.kitPanel === step);
        if (!panel) return false;

        return Array.from(panel.querySelectorAll('[data-kit-card]')).some((card) => !card.hidden);
      }

      visibleSteps() {
        const selectedPath = this.selectedBasePath();
        return this.steps.filter((step) => this.isStepVisible(step, selectedPath));
      }

      nextAvailableStep(fromStep, direction) {
        const visible = this.visibleSteps();
        const currentIndex = visible.indexOf(fromStep);

        if (currentIndex === -1) {
          return direction > 0 ? visible[0] : visible[visible.length - 1];
        }

        const nextIndex = Math.min(Math.max(currentIndex + direction, 0), visible.length - 1);
        return visible[nextIndex];
      }

      renumberVisibleSteps() {
        let counter = 1;

        this.stepButtons.forEach((button) => {
          if (button.hidden || button.closest('li')?.hidden) return;

          const number = button.querySelector('span');
          if (number) number.textContent = String(counter);
          counter += 1;
        });
      }

      showStep(step, shouldScroll = false) {
        this.updateVisibleSteps();

        if (!this.steps.includes(step)) return;
        if (!this.visibleSteps().includes(step)) {
          step = this.nextAvailableStep(step, 1) || 'summary';
        }

        this.currentStep = step;

        this.panels.forEach((panel) => {
          panel.classList.toggle('is-active', panel.dataset.kitPanel === step);
        });

        this.stepButtons.forEach((button) => {
          button.classList.toggle('is-active', button.dataset.stepTarget === step);
        });

        this.updateNavigation();
        this.updateProgress();

        if (step === 'summary') {
          this.updateSummary();
        }

        if (shouldScroll && this.hasInitialized) {
          this.scrollToCurrentPanelTop();
        }
      }

      scrollToCurrentPanelTop() {
        window.requestAnimationFrame(() => {
          const activePanel = this.querySelector('[data-kit-panel].is-active');
          const target = activePanel || this;

          const isMobile = window.matchMedia('(max-width: 749px)').matches;
          const offset = isMobile ? 128 : 24;

          const top = target.getBoundingClientRect().top + window.pageYOffset - offset;

          window.scrollTo({
            top: Math.max(0, top),
            behavior: 'smooth',
          });
        });
      }

      goToAdjacentStep(direction) {
        this.updateVisibleSteps();
        const nextStep = this.nextAvailableStep(this.currentStep, direction);
        if (nextStep) this.showStep(nextStep, true);
      }

      updateNavigation() {
        const visible = this.visibleSteps();
        const currentIndex = visible.indexOf(this.currentStep);

        if (this.prevButton) {
          this.prevButton.disabled = currentIndex <= 0;
          this.prevButton.removeAttribute('hidden');
        }

        if (this.nextButton) {
          this.nextButton.hidden = this.currentStep === 'summary';
          this.nextButton.disabled = false;
        }
      }

      progressSteps() {
        const selectedPath = this.selectedBasePath();

        if (!selectedPath) {
          return ['base', 'inspection', 'precision', 'liquids', 'summary'];
        }

        const visible = this.visibleSteps();
        return visible.length ? visible : ['base', 'summary'];
      }

      updateProgress() {
        if (!this.progressText && !this.progressFill && !this.progressBar) return;

        const progressSteps = this.progressSteps();
        const total = Math.max(progressSteps.length, 1);
        let currentIndex = progressSteps.indexOf(this.currentStep);

        if (currentIndex === -1) {
          const realIndex = this.steps.indexOf(this.currentStep);
          currentIndex = progressSteps.reduce((nearest, step, index) => {
            const stepIndex = this.steps.indexOf(step);
            if (stepIndex <= realIndex) return index;
            return nearest;
          }, 0);
        }

        const current = Math.min(Math.max(currentIndex + 1, 1), total);
        const percent = Math.round((current / total) * 100);

        if (this.progressText) {
          this.progressText.textContent = `Step ${current} di ${total}`;
        }

        if (this.progressFill) {
          this.progressFill.style.width = `${percent}%`;
        }

        if (this.progressBar) {
          this.progressBar.setAttribute('aria-valuenow', String(percent));
        }
      }

      selectedItems() {
        const selectedCards = [
          ...this.baseCards.filter((card) => card.classList.contains('is-selected')),
          ...this.accessoryCards.filter((card) => !card.hidden && card.classList.contains('is-selected')),
        ];

        return selectedCards
          .filter((card) => card.dataset.available === 'true' && card.dataset.variantId)
          .map((card) => {
            const quantityInput = card.querySelector('[data-kit-quantity]');
            const quantity = card.matches('[data-kit-base]') ? 1 : Math.max(1, parseInt(quantityInput?.value || '1', 10));

            return {
              id: Number(card.dataset.variantId),
              quantity,
              price: parseInt(card.dataset.price || '0', 10),
              title: card.querySelector('.card__heading')?.textContent.trim() || '',
              variantTitle:
                card.dataset.summaryVariantTitle ||
                card.dataset.variantTitle ||
                card.querySelector('[data-variant-select]')?.selectedOptions[0]?.textContent.trim() ||
                '',
            };
          });
      }

      updateSummary() {
        const items = this.selectedItems();
        const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

        this.totalElements.forEach((element) => {
          element.textContent = this.formatMoney(total);
        });

        this.renderList(this.summaryList, items);
        this.renderList(this.miniList, items, true);
        this.renderList(this.mobileList, items, true);

        if (this.emptySummary) this.emptySummary.hidden = items.length > 0;
      }

      renderList(list, items, compact = false) {
        if (!list) return;

        if (!items.length) {
          list.innerHTML = '<li class="cc-kit__mini-item"><span>Nessun prodotto selezionato.</span></li>';
          return;
        }

        list.innerHTML = items
          .map((item) => {
            const hasVariantText = item.variantTitle && item.variantTitle !== 'Default Title';
            const title = hasVariantText ? `${item.title} - ${item.variantTitle}` : item.title;
            const className = compact ? 'cc-kit__mini-item' : 'cc-kit__summary-item';

            return `
              <li class="${className}">
                <div>
                  <strong>${this.escape(title)}</strong>
                  <span>Quantità ${item.quantity}</span>
                </div>
                <strong>${this.formatMoney(item.price * item.quantity)}</strong>
              </li>
            `;
          })
          .join('');
      }

      toggleMobileDrawer(open) {
        if (!this.mobileDrawer) return;

        this.mobileDrawer.hidden = !open;
        this.mobileDrawer.classList.toggle('is-open', open);
        document.documentElement.classList.toggle('cc-kit-mobile-drawer-open', open);
      }

      addToCart() {
        const selectedBase = this.baseCards.find((card) => card.classList.contains('is-selected'));

        if (!selectedBase) {
          this.setMessage('Scegli una base prima di aggiungere il kit al carrello.', true);
          this.showStep('base', true);
          return;
        }

        const itemsById = new Map();

        this.selectedItems().forEach((item) => {
          const existing = itemsById.get(item.id);

          if (existing) {
            existing.quantity += item.quantity;
          } else {
            itemsById.set(item.id, { id: item.id, quantity: item.quantity });
          }
        });

        const items = Array.from(itemsById.values());

        if (!items.length) return;

        this.addButton?.setAttribute('aria-disabled', 'true');
        this.addButton?.classList.add('loading');
        this.setMessage('Aggiunta al carrello in corso...', false);

        const cart = document.querySelector('cart-drawer');
        const sections = cart
          ? cart.getSectionsToRender().map((section) => section.id)
          : ['cart-icon-bubble'];

        const body = JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        });

        fetch(`${window.routes.cart_add_url}`, {
          ...this.fetchConfig(),
          body,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              throw new Error(response.description || window.cartStrings?.error || 'Errore carrello');
            }

            if (cart && response.sections) {
              cart.renderContents(response);
            } else if (response.sections) {
              this.renderSection('cart-icon-bubble', response.sections['cart-icon-bubble']);
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'craftcard-kit-configurator',
                cartData: response,
              });
            }

            this.setMessage('Kit aggiunto al carrello.', false);
          })
          .catch((error) => {
            this.setMessage(error.message || window.cartStrings?.error || 'Non è stato possibile aggiungere il kit.', true);
          })
          .finally(() => {
            this.addButton?.classList.remove('loading');
            this.addButton?.removeAttribute('aria-disabled');
          });
      }

      renderSection(id, html) {
        if (!html) return;

        const target = document.getElementById(id);
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');

        if (target && source) {
          target.innerHTML = source.innerHTML;
        }
      }

      fetchConfig() {
        if (typeof fetchConfig === 'function') return fetchConfig('javascript');

        return {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        };
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
        const value = (Number(cents || 0) / 100).toFixed(2);
        const commaValue = value.replace('.', ',');

        return format
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, commaValue)
          .replace(/\{\{\s*amount\s*\}\}/, value)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(cents || 0) / 100)))
          .replace(/<[^>]*>/g, '');
      }

      setMessage(text, isError) {
        if (!this.message) return;

        this.message.textContent = text;
        this.message.classList.toggle('is-error', Boolean(isError));
      }

      capitalize(value) {
        return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
      }

      escape(value) {
        const div = document.createElement('div');
        div.textContent = value;
        return div.innerHTML;
      }
    }
  );
}